import { useEffect, useRef, useState } from "react";
import "./chart.css";

/** Measures the rendered width of a container so charts can be pixel-accurate. */
export function useContainerWidth<T extends HTMLElement>(): {
  ref: React.RefObject<T | null>;
  width: number;
} {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, width };
}

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "3PM" (iOS `ha`). */
export function formatHour(date: Date): string {
  const hours = date.getHours();
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}${hours < 12 ? "AM" : "PM"}`;
}

/** "Jul 12" (iOS `MMM d`). */
export function formatMonthDay(date: Date): string {
  return `${monthNames[date.getMonth()]} ${date.getDate()}`;
}

/** "Jul 12, 3:05 PM" (iOS `MMM d, h:mm a`). */
export function formatTooltipDate(date: Date): string {
  const hours = date.getHours();
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${formatMonthDay(date)}, ${hour12}:${minutes} ${hours < 12 ? "AM" : "PM"}`;
}

/** Contiguous index runs where `active(i)` holds, as inclusive [start, end] pairs. */
export function findRuns(count: number, active: (index: number) => boolean): Array<[number, number]> {
  const runs: Array<[number, number]> = [];
  let start: number | null = null;
  for (let i = 0; i < count; i += 1) {
    if (active(i)) {
      if (start === null) start = i;
    } else if (start !== null) {
      runs.push([start, i - 1]);
      start = null;
    }
  }
  if (start !== null) runs.push([start, count - 1]);
  return runs;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
